import Title from '@cohort/components-xps/components/Title';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {OwnedDigitalAssetVisualsGroup} from '@cohort/wallet/components/OwnedDigitalAssetVisualsGroup';
import {PerkVisual} from '@cohort/wallet/components/space/PerkVisual';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {AccessesAndOwnership} from '@cohort/wallet/lib/Utils';
import type {PerkWDto} from '@cohort/wallet-schemas/perk';
import React, {Fragment} from 'react';

interface AboutPerkPanelProps {
  perk: PerkWDto;
  accessesAndOwnerships: AccessesAndOwnership[];
  orientation?: 'horizontal' | 'vertical';
  children?: React.ReactNode;
}

export const AboutPerkPanel: React.FC<AboutPerkPanelProps> = ({
  perk,
  accessesAndOwnerships,
  orientation = 'vertical',
  children,
}) => {
  const {backgroundColor, hasDarkBg} = useThemeContext();
  const hasBanner = perk.bannerFileKey !== null || perk.thumbnailFileKey !== null;

  if (orientation === 'horizontal') {
    return (
      <div className="flex lg:justify-center">
        <div
          className={classNames(
            'grid gap-8',
            hasBanner
              ? '[grid-template-columns:minmax(200px,380px)_min-content_minmax(300px,1fr)] lg:max-w-[50vw]'
              : 'w-full grid-cols-1 lg:w-[50vw]'
          )}
        >
          {hasBanner && (
            <Fragment>
              <PerkVisual perk={perk} />
              <div
                className={classNames(
                  'h-full border-l',
                  hasDarkBg ? 'sm:border-white/20' : 'sm:border-black/20'
                )}
              ></div>
            </Fragment>
          )}
          <div className="flex flex-col justify-between gap-4">
            <div>
              <Title content={perk.displayName} />
              <OwnedDigitalAssetVisualsGroup
                perkAccessesAndOwnerships={accessesAndOwnerships}
                imageDominantColor={backgroundColor}
                backgroundPageColorPage={backgroundColor}
              />
            </div>
            {perk.description !== null && (
              <div
                dangerouslySetInnerHTML={{__html: perk.description}}
                className={classNames('prose prose-base text-left', hasDarkBg && 'prose-invert')}
              />
            )}
            <div className="grid w-full grid-cols-1">{children}</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col space-y-6">
      <PerkVisual perk={perk} />
      <div className="flex flex-col space-y-2">
        <Title content={perk.displayName} />
        <OwnedDigitalAssetVisualsGroup
          perkAccessesAndOwnerships={accessesAndOwnerships}
          imageDominantColor={backgroundColor}
          backgroundPageColorPage={backgroundColor}
        />
      </div>
      {perk.description !== null && (
        <div
          dangerouslySetInnerHTML={{__html: perk.description}}
          className={classNames('prose prose-base text-left', hasDarkBg && 'prose-invert')}
        />
      )}
      {children}
    </div>
  );
};

export default AboutPerkPanel;
